/**
 * Три тёмных направления из макета. Каждое меняет палитру, радиусы,
 * типографику и цвета тепловой карты целиком — переключение чипами в шапке.
 */
export type ThemeKey = 'cobalt' | 'phosphor' | 'graphite';

export interface Theme {
  label: string;
  hint: string;
  vars: Record<string, string>;
  /** Пять ступеней тепловой карты: офлайн, <30, 30–69, 70–89, 90+ */
  heat: [string, string, string, string, string];
  /** Цвет подписи на соответствующей ступени */
  heatInk: [string, string, string, string, string];
}

export const THEMES: Record<ThemeKey, Theme> = {
  cobalt: {
    label: 'Cobalt',
    hint: 'Кобальтовый мониторинг: графики и синий акцент',
    vars: {
      '--bg': '#0c0e12', '--bgFade': 'rgba(12,14,18,.92)', '--panel': '#12151b', '--panel2': '#171b23',
      '--bar': '#1b202a', '--code': '#0b0d11', '--codeInk': '#d8e3f5',
      '--line': '#232833', '--line2': '#333a49', '--grid': '#1e232c', '--track': '#232833',
      '--hover': 'rgba(255,255,255,.05)', '--hover2': '#1c2129',
      '--ink': '#e7eaf0', '--ink2': '#aab3c2', '--ink3': '#7d8798', '--ink4': '#5b6373',
      '--acc': '#4c8dff', '--accInk': '#08111f', '--accSoft': 'rgba(76,141,255,.14)',
      '--ok': '#35d07f', '--warn': '#ffb84d', '--bad': '#ff5c5c', '--badSoft': 'rgba(255,92,92,.14)',
      '--fs': "'IBM Plex Sans', system-ui, sans-serif", '--fh': "'IBM Plex Sans', system-ui, sans-serif",
      '--fm': "'IBM Plex Mono', ui-monospace, monospace", '--r': '10px', '--rs': '7px'
    },
    heat: ['#1c2431', '#2b4a7a', '#3f7ad1', '#7cb0ff', '#ff5c5c'],
    heatInk: ['#7d8798', '#cfe0ff', '#08111f', '#08111f', '#20060a']
  },
  phosphor: {
    label: 'Phosphor',
    hint: 'NOC-стена: моношрифт, фосфорный зелёный, без украшений',
    vars: {
      '--bg': '#070907', '--bgFade': 'rgba(7,9,7,.93)', '--panel': '#0c0f0c', '--panel2': '#111511',
      '--bar': '#121712', '--code': '#050705', '--codeInk': '#b8f5cf',
      '--line': '#1e2a1e', '--line2': '#2c3d2c', '--grid': '#162016', '--track': '#1a241a',
      '--hover': 'rgba(53,208,127,.08)', '--hover2': '#141b14',
      '--ink': '#dcf0e2', '--ink2': '#9dbba7', '--ink3': '#6f8a78', '--ink4': '#51665a',
      '--acc': '#35d07f', '--accInk': '#04180c', '--accSoft': 'rgba(53,208,127,.14)',
      '--ok': '#35d07f', '--warn': '#ffc247', '--bad': '#ff6b4a', '--badSoft': 'rgba(255,107,74,.14)',
      '--fs': "'IBM Plex Mono', ui-monospace, monospace", '--fh': "'IBM Plex Mono', ui-monospace, monospace",
      '--fm': "'IBM Plex Mono', ui-monospace, monospace", '--r': '3px', '--rs': '2px'
    },
    heat: ['#131c15', '#1f4a30', '#2f8a52', '#7ce0a4', '#ffc247'],
    heatInk: ['#6f8a78', '#b8f5cf', '#04180c', '#04180c', '#2a1c00']
  },
  graphite: {
    label: 'Graphite',
    hint: 'Спокойная тёмная админка: воздух, мягкие панели, бирюза',
    vars: {
      '--bg': '#131417', '--bgFade': 'rgba(19,20,23,.9)', '--panel': '#1a1c20', '--panel2': '#20232a',
      '--bar': '#23262d', '--code': '#12141a', '--codeInk': '#e2e6ee',
      '--line': '#2a2e36', '--line2': '#3a404b', '--grid': '#252932', '--track': '#2a2e36',
      '--hover': 'rgba(255,255,255,.06)', '--hover2': '#252932',
      '--ink': '#f0f1f4', '--ink2': '#b3b8c2', '--ink3': '#868d99', '--ink4': '#646b78',
      '--acc': '#9d8cff', '--accInk': '#120e2a', '--accSoft': 'rgba(157,140,255,.16)',
      '--ok': '#5ed6a4', '--warn': '#f5c26b', '--bad': '#f4736a', '--badSoft': 'rgba(244,115,106,.15)',
      '--fs': "'Manrope', system-ui, sans-serif", '--fh': "'Manrope', system-ui, sans-serif",
      '--fm': "'IBM Plex Mono', ui-monospace, monospace", '--r': '16px', '--rs': '11px'
    },
    heat: ['#232830', '#34305e', '#6b5bd6', '#b4a7ff', '#f4736a'],
    heatInk: ['#868d99', '#ded8ff', '#120e2a', '#120e2a', '#25090a']
  }
};

export const THEME_KEYS = Object.keys(THEMES) as ThemeKey[];

/** CSS-переменные темы + плотность как inline-стиль корневого div. */
export function themeStyle(key: ThemeKey, dense: boolean): React.CSSProperties {
  return { ...THEMES[key].vars, '--pad': dense ? '14px' : '26px' } as React.CSSProperties;
}
